import type { MenschMove, MenschView } from "@bing/engine-mensch";
import {
  armOf,
  MENSCH_PLAYER_COUNTS,
  STEP_HOP_MS as ENGINE_STEP_HOP_MS,
  TOKENS_PER_PLAYER,
  TRACK_LEN,
  YARD,
  type MenschPlayerCount,
} from "@bing/engine-mensch";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { GameIcon } from "../components/GameIcon";
import { displayName, type DisplayNameParts } from "../components/profile-ui";
import { useFitSquare } from "../game/use-fit-square";
import {
  armColor,
  cellForStep,
  GRID,
  homeCellsForArm,
  MENSCH_ARMS,
  rotateBy,
  SEAT_COLORS,
  seatColor,
  seatOnArm,
  paletteShift,
  startIndexForArm,
  trackCells,
  viewTurns,
  yardCellsForArm,
  type Cell,
} from "../game/mensch-board";
import { DIE_THROW_MS, Die3D } from "../game/backgammon-dice";
import { botOrdinal } from "../game/opponent-number";
import { useMenschSfx } from "../game/mensch-sfx";
import { KEEPS_ITS_ORDER, nameBoxDir } from "../game/name-room";
import { TurnTimer, type TableTurnTimer } from "../game/online-lifecycle";
import { fa } from "../i18n/fa";

/* ------------------------------------------------------------------ *
 * منچ — the tap-to-move board, PORTRAIT.
 *
 * The cross is painted from `game/mensch-board.ts`, which owns ALL the
 * geometry as pure data (and is unit-tested on its own). Nothing here
 * computes a coordinate; this file only turns cells into pixels.
 *
 * A turn is: tap «تاس بریز» → the die lands → tap one of the highlighted
 * tokens. The server is authoritative for both the roll and the legal
 * token list (`view.legalTokens`), so the board never guesses a rule.
 * ------------------------------------------------------------------ */

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"] as const;
function faNum(value: number | string): string {
  return String(value).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

/** Base <button> reset (Tailwind preflight is off → reset explicitly). */
const BTN = "appearance-none m-0 cursor-pointer";

/**
 * Height inside the board column that the chrome BELOW the board already owns: the die slot (48),
 * the hint line (30), the capture-notice reserve (30), and the column's three 12px (`gap-3`) gaps.
 *
 * All four are FIXED by design — each carries a "so the board never resizes" comment — so a
 * constant is exact here. It is reserved from the fit's HEIGHT axis only: without it the square was
 * fitted to the column's FULL height, the chrome was then laid out below it, and the column's
 * `justify-center` + `overflow-hidden` clipped the board's TOP edge, which reads on screen as the
 * board sliding under the player strips. KEEP IN STEP with the four heights below.
 */
const BOARD_CHROME_H = 48 + 30 + 30 + 3 * 12;

/* ---- MOTION TIMINGS ----------------------------------------------------------------
 * Deliberately unhurried: at the old 520ms/220ms the die was a blur and a token simply
 * appeared on its new square. These are the CLIENT half of a contract — the engine's
 * `postMoveDelayMs` must stay >= the beat it corresponds to, or the next bot moves on top
 * of an animation that is still running. Change one, check the other.
 * ------------------------------------------------------------------------------------ */

/** The die tumble — the shared 3D cube's own throw length (see game/backgammon-dice). */
export const DIE_ROLL_MS = DIE_THROW_MS;
/**
 * How long a thrown die STAYS on screen after the engine has cleared it.
 *
 * The die used to be visible only while `view.die !== null`, and that window is shorter than
 * the throw itself — an opponent's die was pulled off the table mid-tumble, and your own
 * vanished before you could read a dud roll. It must outlast the animation and then hold
 * long enough to actually be read.
 */
export const DIE_LINGER_MS = DIE_THROW_MS + 900;
/** A dead roll passes itself, once the player has had time to see WHY. */
export const AUTO_PASS_MS = DIE_THROW_MS + 1100;
/** How long a capture announcement stays up — long enough to read, short enough not to stale. */
export const CAPTURE_NOTICE_MS = 3200;
/** The beat between the capturing token ARRIVING and the captured one letting go of the
 *  square, so the two are never seen standing on it together. */
export const CAPTURE_LIFT_MS = 260;
/** How long a freshly-lit home indicator flares — a lamp turning on. */
export const HOME_FLARE_MS = 1200;
/**
 * A token WALKS the track, one square at a time, instead of sliding straight to its
 * destination. Sliding was quick but unreadable — you could not see how far a token had
 * gone, or which squares it passed. Counting the squares is the whole point of the roll.
 *
 * `STEP_HOP_MS` is the ENGINE's `STEP_HOP_MS` (its pacing multiplies it by the distance
 * travelled, so the next player never acts mid-walk). The hop transition is a touch shorter
 * so each square settles before the next begins.
 */
export const STEP_HOP_MS = ENGINE_STEP_HOP_MS;
export const TOKEN_SLIDE_MS = 170;
/** A captured token's longer journey back to its yard — it should be seen leaving. */
export const TOKEN_RETURN_MS = 620;

/**
 * Table-size picker (۲/۳/۴ نفره). Shown on the solo start screen and on the online
 * mode chooser, so the player always decides how many are at the table. Segmented, to
 * match `DifficultySelect` sitting directly above it.
 */
export function MenschPlayersSelect({
  value,
  onChange,
  disabled = false,
}: {
  value: number;
  onChange: (players: MenschPlayerCount) => void;
  disabled?: boolean;
}): ReactNode {
  const label = (n: number): string =>
    n === 2 ? fa.mensch.players2 : n === 3 ? fa.mensch.players3 : fa.mensch.players4;
  return (
    <div
      role="radiogroup"
      aria-label={fa.mensch.players}
      data-testid="mensch-players"
      className="flex w-full gap-1 rounded-xl border border-outline-variant bg-surface-container/70 p-1"
    >
      {MENSCH_PLAYER_COUNTS.map((n) => {
        const active = n === value;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(n)}
            className={`${BTN} flex-1 rounded-lg px-2 py-2 text-body-md font-bold transition-colors ${
              active
                ? "bg-secondary text-ink-on-gold shadow-sm"
                : "bg-transparent text-on-surface-variant hover:text-on-surface"
            } ${disabled ? "opacity-50" : ""}`}
          >
            {label(n)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * ITEM 8 — pick YOUR colour. Presentation only: you always sit at the near corner and the
 * palette rotates around you. (It shares the start screen with منچ's TWO-level opponent picker.)
 */
export function MenschColorSelect({
  value,
  onChange,
}: {
  value: number;
  onChange: (color: number) => void;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={fa.mensch.color}
      data-testid="mensch-colors"
      className="flex w-full justify-center gap-3 rounded-xl border border-outline-variant bg-surface-container/70 p-2"
    >
      {MENSCH_ARMS.map((arm) => {
        const tone = SEAT_COLORS[arm] ?? SEAT_COLORS[0]!;
        const active = arm === value;
        return (
          <button
            key={arm}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={fa.mensch.colorNames[arm] ?? String(arm)}
            onClick={() => onChange(arm)}
            className={`${BTN} rounded-full transition-transform active:scale-95`}
            style={{
              width: 34,
              height: 34,
              background: `radial-gradient(circle at 32% 30%, ${tone.light} 0%, ${tone.base} 68%, rgba(0,0,0,0.55) 100%)`,
              border: active ? "2px solid #fffdf0" : "1px solid rgba(0,0,0,0.45)",
              boxShadow: active
                ? `0 0 0 2px ${tone.base}, 0 0 10px ${tone.base}`
                : "0 2px 4px rgba(0,0,0,0.45)",
            }}
          />
        );
      })}
    </div>
  );
}

/** What we call a seat: their real name if we have one, else «شما» / «حریف N». ISSUE B — the bot
 *  number is ABSOLUTE (its rank among the bot seats), so the same bot is the same «حریف N» on every
 *  player's screen, not a number relative to whoever is looking. Exported for the end card's rows
 *  (card-98 item 12), so the card and the board call a seat the same. */
export function menschSeatName(
  seat: number,
  you: number,
  seatCount: number,
  self: MenschIdentity | null | undefined,
  players: readonly (MenschIdentity | null)[] | null | undefined,
): string {
  const parts = seat === you ? self : (players?.[seat] ?? null);
  if (parts) return displayName(parts);
  if (seat === you) return fa.mensch.you;
  const seatIsBot = (s: number): boolean => s !== you && !(players?.[s] ?? null);
  return `${fa.mensch.opponent} ${faNum(botOrdinal(seat, seatCount, seatIsBot))}`;
}

export interface MenschIdentity extends DisplayNameParts {
  avatarId?: string | null;
  avatarPhotoId?: string | null;
  /** Official/verified account → the disc wears the badge. */
  isOfficial?: boolean;
}

export function MenschTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  players,
  myColor,
  timer,
}: {
  view: MenschView;
  legal: readonly MenschMove[];
  onMove: (move: MenschMove) => void;
  onLeave: () => void;
  self?: MenschIdentity | null;
  /** Per-seat identities (online). Omitted → «شما» + bot labels. */
  players?: readonly (MenschIdentity | null)[] | null;
  /** ITEM 8 — the arm-colour the LOCAL player chose (0..3). The palette is rotated so their
   *  own arm wears it and the others shift along; seating never moves. */
  myColor?: number;
  /** ITEM 7 — the live online turn countdown, rendered on the HUD line. Omitted offline. */
  timer?: TableTurnTimer | null;
}): ReactNode {
  // The board is a SQUARE grid, so it must fit BOTH axes. useBoxHeight sized it from the height
  // alone, and the flex row then shrank the width — laying cells out for a wider board than was
  // actually painted, which pushed the outer tokens off the edge.
  const fit = useFitSquare<HTMLDivElement>(420, 12, BOARD_CHROME_H);
  // Rotate the palette so the local player's own arm wears the colour they picked.
  // ISSUE 1 — ONLINE, every phone must paint each arm the SAME colour: seat S's tokens are
  // always SEAT_COLORS[armOf(S)], no per-viewer rotation. `myColor ?? 0` did NOT do that — it
  // still fed `paletteShift(armOf(you), 0)`, which rotates the whole palette by the VIEWER'S own
  // arm, so the bots (حریف) came out a different colour on each player's screen. A rotation only
  // belongs OFFLINE, where the player picked a colour for their own arm; online (no `myColor`)
  // there must be no rotation at all.
  const shift =
    myColor === undefined ? 0 : paletteShift(armOf(view.you, view.tokens.length), myColor);
  const armTone = (arm: number): { base: string; light: string } => armColor(arm, shift);
  const seatTone = (seat: number): { base: string; light: string } =>
    armTone(armOf(seat, view.tokens.length));
  // Sound lives HERE so it covers solo and online alike (the shared-table pattern).
  useMenschSfx(view);
  const you = view.you;
  const isMyTurn = view.turn === you && view.status === "playing";
  const canRoll = legal.some((m) => m.kind === "roll");
  const canPass = legal.some((m) => m.kind === "pass");

  // A THROW animates whenever the engine's monotonic `rollCount` moves.
  //
  // It used to key on the die's VALUE changing, which was wrong twice over: rolling a 3
  // and then another 3 is two throws but one value, so the second never animated; and the
  // effect's cleanup cancelled the reset timer when the die went back to null at the end of
  // the turn, leaving `rolling` stuck true — after which every `setRolling(true)` was a
  // no-op, the class never left the DOM, and NOTHING animated again all game.
  //
  // `throwId` increments per throw and is used as the element KEY, so React tears the die
  // down and rebuilds it: the CSS animation restarts even on a repeated value.
  const [throwId, setThrowId] = useState(0);
  const [rolling, setRolling] = useState(false);
  const prevRolls = useRef<number | undefined>(undefined);
  const rollTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    // Adopt at mount: a resume must not replay a throw nobody witnessed.
    if (prevRolls.current === undefined) {
      prevRolls.current = view.rollCount;
      return;
    }
    if (view.rollCount === prevRolls.current) return;
    prevRolls.current = view.rollCount;
    setThrowId((n) => n + 1);
    setRolling(true);
    window.clearTimeout(rollTimer.current);
    rollTimer.current = window.setTimeout(() => setRolling(false), DIE_ROLL_MS);
  }, [view.rollCount]);
  // Clear the timer ONLY on unmount — never on a re-render, or `rolling` sticks on.
  useEffect(() => () => window.clearTimeout(rollTimer.current), []);
  // The die HOLDS after the engine clears it, so a throw is always seen through to the end.
  // Without this the die is on screen only while `view.die !== null` — a window shorter than
  // the throw animation, which is why an opponent's roll was cut off mid-tumble and a dud roll
  // of your own disappeared before you could read it.
  const [held, setHeld] = useState<{ value: number; roller: number } | null>(null);
  useEffect(() => {
    if (view.die !== null) {
      // Capture WHOSE die it is now, while the engine still says so.
      setHeld({ value: view.die, roller: view.roller ?? view.turn });
      return undefined;
    }
    // Depends only on (die, rollCount), and rollCount cannot move while the die is null —
    // a roll sets it — so this timer arms exactly once per cleared die and is never
    // restarted underneath itself.
    const id = window.setTimeout(() => setHeld(null), DIE_LINGER_MS);
    return () => window.clearTimeout(id);
  }, [view.die, view.rollCount, view.roller, view.turn]);

  // A CAPTURE gets its own announcement line. It is the single most consequential thing that
  // can happen, and it was previously only a ripple + a sound — nothing said WHO hit WHOM, and
  // nothing survived long enough to read. Kept OFF the live status line so a one-off event is
  // never confused with the rolling turn prompt.
  const [capture, setCapture] = useState<{ by: number; of: number; key: number } | null>(null);
  const prevCapture = useRef<number | undefined>(undefined);
  const captureKey = useRef(0);
  useEffect(() => {
    const e = view.lastEvent;
    if (prevCapture.current === undefined) {
      prevCapture.current = view.moveCount; // adopt at mount — a resume never re-announces
      return undefined;
    }
    if (view.moveCount === prevCapture.current) return undefined;
    prevCapture.current = view.moveCount;
    if (!e?.captured) return undefined;
    captureKey.current += 1;
    setCapture({ by: e.seat, of: e.captured.seat, key: captureKey.current });
    const id = window.setTimeout(() => setCapture(null), CAPTURE_NOTICE_MS);
    return () => window.clearTimeout(id);
    // DEPEND ON THE COUNTER ALONE. `view.lastEvent` is a NEW OBJECT on every server frame, so
    // listing it here re-ran this effect constantly — and the cleanup then cancelled the timers
    // while the early-return skipped re-arming them. That is why the capture notice never
    // disappeared, and why a frame arriving mid-walk froze a token between squares.
  }, [view.moveCount]);

  // WALK the moved token through every square it crossed. The view already places it at its
  // destination, so this temporarily overrides its position and steps it forward.
  const [walk, setWalk] = useState<{ seat: number; token: number; step: number } | null>(null);
  /**
   * A captured token is sent to its yard by the ENGINE the instant the move applies — so it
   * vanished before the capturing token had even set off, and the whole thing read as an
   * instant swap. This holds the victim on the contested square (rendered from the CAPTURING
   * seat's frame of reference, which is the same absolute square) until the capturer arrives;
   * only then is it released, and its normal glide carries it home.
   */
  const [victim, setVictim] = useState<{
    seat: number;
    token: number;
    by: number;
    at: number;
  } | null>(null);
  const prevWalk = useRef<number | undefined>(undefined);
  const captureTimers = useRef<number[]>([]);
  /* ITEM 4 — this MUST be a LAYOUT effect.
   *
   * The incoming frame already places the token on its DESTINATION, so with a plain
   * `useEffect` the browser painted that position first: the token lurched toward the far
   * square — the transform interpolates in a straight line, so it cut diagonally across the
   * felt and outside the arms — then snapped back to `from + 1` and walked properly. That is
   * the "jumps outside the board and comes back" glitch, and it was most obvious on a bot's
   * move because it arrives unannounced. A layout effect runs BEFORE paint, so the override
   * is in place for the first painted frame and the destination is never shown early. */
  useEffect(
    () => () => {
      captureTimers.current.forEach((id) => window.clearTimeout(id));
      captureTimers.current = [];
    },
    [],
  );
  useLayoutEffect(() => {
    const e = view.lastEvent;
    if (prevWalk.current === undefined) {
      prevWalk.current = view.moveCount; // adopt at mount — a resume never re-walks
      return undefined;
    }
    // Keyed on the MOVE COUNTER, never on the event's identity: the event is a brand-new
    // object on every frame, so identity made an unrelated update (your next roll) replay
    // the opponent's move — the token visibly snapped back and walked again.
    if (view.moveCount === prevWalk.current) return undefined;
    prevWalk.current = view.moveCount;
    if (!e) return undefined;

    // How long the capturing token takes to arrive: the walk, or a single hop for a release.
    const squares = e.released ? 1 : Math.max(1, e.to - e.from);
    const arrival = squares * STEP_HOP_MS;
    if (e.captured) {
      // Keep the victim standing on the contested square until the capturer lands on it...
      setVictim({ seat: e.captured.seat, token: e.captured.token, by: e.seat, at: e.to });
      // ...then let go, a beat AFTER arrival so the two are never on the square at once.
      const clear = window.setTimeout(() => setVictim(null), arrival + CAPTURE_LIFT_MS);
      captureTimers.current.push(clear);
    }
    // A release pops straight out of the yard onto the start square: one move, not a walk.
    if (e.released || e.to - e.from < 2) return undefined;
    const timers: number[] = [];
    for (let step = e.from + 1; step < e.to; step += 1) {
      const at = step;
      timers.push(
        window.setTimeout(
          () => setWalk({ seat: e.seat, token: e.token, step: at }),
          (at - e.from) * STEP_HOP_MS,
        ),
      );
    }
    // Hand back to the view's own (final) position once the walk is done.
    timers.push(window.setTimeout(() => setWalk(null), (e.to - e.from) * STEP_HOP_MS));
    // Start on the FIRST intermediate square immediately, so the token leaves at once.
    setWalk({ seat: e.seat, token: e.token, step: e.from + 1 });
    return () => timers.forEach((id) => window.clearTimeout(id));
    // DEPEND ON THE COUNTER ALONE. `view.lastEvent` is a NEW OBJECT on every server frame, so
    // listing it here re-ran this effect constantly — and the cleanup then cancelled the timers
    // while the early-return skipped re-arming them. That is why the capture notice never
    // disappeared, and why a frame arriving mid-walk froze a token between squares.
  }, [view.moveCount]);

  // ITEM 5: light the indicator when a token gets home. `view.finished[seat]` already counts
  // every token in the home row (40..43), so the PIPS are the right indicator — they just
  // needed to visibly turn on. Driven off the count so it fires for all four arrivals, not
  // only the one that reaches the innermost square.
  const [litSeat, setLitSeat] = useState<number | null>(null);
  const prevHome = useRef<readonly number[] | undefined>(undefined);
  useEffect(() => {
    const before = prevHome.current;
    prevHome.current = view.finished;
    if (before === undefined) return undefined; // adopt at mount — a resume never flares
    const seat = view.finished.findIndex((n, i) => n > (before[i] ?? 0));
    if (seat < 0) return undefined;
    setLitSeat(seat);
    const id = window.setTimeout(() => setLitSeat(null), HOME_FLARE_MS);
    return () => window.clearTimeout(id);
  }, [view.finished]);

  const shownDie = view.die !== null ? view.die : (held?.value ?? null);
  // Whose die is on the table.
  const rollerSeat = view.die !== null ? (view.roller ?? view.turn) : (held?.roller ?? view.turn);

  // A dead roll (nothing playable) passes ITSELF once the player has seen the number. A button
  // for it was pure friction — there is only one thing that can happen, so the game does it.
  const autoPassed = useRef<number>(-1);
  useEffect(() => {
    if (!canPass || autoPassed.current === view.rollCount) return undefined;
    const at = view.rollCount;
    const id = window.setTimeout(() => {
      autoPassed.current = at;
      onMove({ kind: "pass" });
    }, AUTO_PASS_MS);
    return () => window.clearTimeout(id);
  }, [canPass, view.rollCount, onMove]);

  // Design system M5: a capture lands as a radial IMPACT ripple on the felt, at the square the
  // capturing token arrived on. Driven by the engine's authoritative `lastEvent.captured` — never
  // inferred from diffing positions, so it can't fire on a resume or a re-sent frame.
  const [ripple, setRipple] = useState<{ col: number; row: number; key: number } | null>(null);
  const prevEvent = useRef<number | undefined>(undefined);
  const rippleKey = useRef(0);
  useEffect(() => {
    const ev = view.lastEvent;
    // Adopt at mount: only a WITNESSED capture ripples.
    if (prevEvent.current === undefined) {
      prevEvent.current = view.moveCount;
      return;
    }
    if (view.moveCount !== prevEvent.current && ev?.captured) {
      const cell = cellForStep(ev.seat, ev.to, ev.token, view.tokens.length);
      rippleKey.current += 1;
      setRipple({ col: cell.col, row: cell.row, key: rippleKey.current });
      const id = window.setTimeout(() => setRipple(null), 620);
      prevEvent.current = view.moveCount;
      return () => window.clearTimeout(id);
    }
    prevEvent.current = view.moveCount;
    return undefined;
    // DEPEND ON THE COUNTER ALONE. `view.lastEvent` is a NEW OBJECT on every server frame, so
    // listing it here re-ran this effect constantly — and the cleanup then cancelled the timers
    // while the early-return skipped re-arming them. That is why the capture notice never
    // disappeared, and why a frame arriving mid-walk froze a token between squares.
  }, [view.moveCount]);

  // How many are actually at this table (2, 3 or 4) — the engine deals one token row per
  // seated player, so the view itself is the source of truth. Never assume four.
  const seatCount = view.tokens.length;
  // Spin the WHOLE board so your own colour sits at your own side. Purely presentational:
  // no seat is renumbered, and every opponent keeps their true position relative to you.
  const turns = viewTurns(you, seatCount);

  // Board pixel maths: the cross is a GRID×GRID square sized to the measured box.
  const side = fit.size > 0 ? fit.size : 320;
  const cellPx = side / GRID;
  /** A grid cell → pixels, AFTER the board rotation. Everything on the felt goes through here. */
  const pos = (cell: Cell): { left: number; top: number } => {
    const r = rotateBy(cell, turns);
    return { left: r.col * cellPx, top: r.row * cellPx };
  };

  const track = trackCells();

  /** One painted square of the cross. */
  const square = (key: string, cell: Cell, fill: string, ring?: string, opacity = 1): ReactNode => {
    const p = pos(cell);
    return (
      <span
        key={key}
        aria-hidden
        data-mensch-cell={key}
        style={{
          position: "absolute",
          left: p.left + cellPx * 0.06,
          top: p.top + cellPx * 0.06,
          width: cellPx * 0.88,
          height: cellPx * 0.88,
          borderRadius: cellPx * 0.22,
          background: fill,
          opacity,
          boxShadow: ring ? `inset 0 0 0 2px ${ring}` : "inset 0 1px 2px rgba(0,0,0,0.25)",
        }}
      />
    );
  };

  /** A token disc. Tappable only when the engine says it is legal. */
  const token = (seat: number, index: number, step: number): ReactNode => {
    const walking = walk !== null && walk.seat === seat && walk.token === index;
    const held = victim !== null && victim.seat === seat && victim.token === index;
    const shownStep = walking ? walk.step : step;
    // A token being captured stays put — drawn on the CAPTURER's square, which is the same
    // absolute square it is standing on — until the capturing token actually gets there.
    const p = held
      ? pos(cellForStep(victim.by, victim.at, index, seatCount))
      : pos(cellForStep(seat, shownStep, index, seatCount));
    const colour = seatTone(seat);
    const playable = isMyTurn && seat === you && view.legalTokens.includes(index);
    const size = cellPx * 0.74;
    return (
      <button
        key={`t-${seat}-${index}`}
        type="button"
        data-testid={`token-${seat}-${index}`}
        disabled={!playable}
        onClick={() => playable && onMove({ kind: "move", token: index })}
        className={`${BTN} mensch-token${playable ? " mensch-token--legal" : ""}`}
        aria-label={`${fa.mensch.token} ${faNum(index + 1)}`}
        style={{
          position: "absolute",
          // Positioned with a TRANSFORM, not left/top. Animating left/top re-runs layout for
          // the entire board on every frame of every hop — with sixteen tokens that is the
          // single most expensive thing on the screen. A transform stays on the compositor.
          left: 0,
          top: 0,
          transform: `translate3d(${p.left + (cellPx - size) / 2}px, ${p.top + (cellPx - size) / 2}px, 0)`,
          width: size,
          height: size,
          borderRadius: "50%",
          background: `radial-gradient(circle at 32% 30%, ${colour.light} 0%, ${colour.base} 68%, rgba(0,0,0,0.55) 100%)`,
          border: `1px solid rgba(0,0,0,0.45)`,
          boxShadow: playable
            ? `0 0 0 2px #fffdf0, 0 3px 6px rgba(0,0,0,0.5)`
            : `0 2px 4px rgba(0,0,0,0.45)`,
          cursor: playable ? "pointer" : "default",
          // Tokens slide between cells; the engine drives the position, CSS does the motion.
          // A walking token hops square to square; anything else (notably a captured token
          // travelling back to its yard) gets a longer glide so it is seen leaving.
          transition: `transform ${walking ? TOKEN_SLIDE_MS : TOKEN_RETURN_MS}ms cubic-bezier(.2,.7,.3,1), opacity 220ms linear`,
          zIndex: playable ? 6 : 5,
        }}
      />
    );
  };

  /** A compact per-seat strip: avatar, name, tokens-home count, turn marker. */
  /** What we call a seat: their real name if we have one, else «شما» / «حریف N». ISSUE B — the
   *  bot number is ABSOLUTE (its rank among the bot seats), so the same bot is the same «حریف N»
   *  on every player's screen, not a number relative to whoever is looking. */
  const seatName = (seat: number): string => menschSeatName(seat, you, seatCount, self, players);

  const strip = (seat: number): ReactNode => {
    const colour = seatTone(seat);
    const parts = seat === you ? self : (players?.[seat] ?? null);
    const active = view.turn === seat && view.status === "playing";
    return (
      <div
        key={`s-${seat}`}
        data-testid={`mensch-strip-${seat}`}
        className="flex items-center gap-1.5 rounded-lg px-2 py-1"
        style={{
          background: active ? "rgba(255,255,255,0.08)" : "transparent",
          boxShadow: active ? `inset 0 0 0 1px ${colour.base}` : undefined,
          minWidth: 0,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: colour.base,
            flex: "none",
          }}
        />
        <Avatar
          avatarId={parts?.avatarId ?? "fox"}
          avatarPhotoId={parts?.avatarPhotoId}
          size={26}
          official={parts?.isOfficial === true}
          ringClass={active ? "border-secondary" : "border-outline-variant"}
        />
        {/* A long Latin name keeps its beginning, and only its cut changes: `nameBoxDir`. */}
        <span
          dir={nameBoxDir(seatName(seat))}
          className="min-w-0 flex-1 truncate text-caption text-on-surface"
          style={{ textAlign: "right" }}
        >
          <span style={KEEPS_ITS_ORDER}>{seatName(seat)}</span>
        </span>
        {/* Design system: token progress reads as four pips, not a «۰/۴» counter — you can see
            at a glance how close every player is without parsing a number. */}
        <span
          aria-label={`${faNum(view.finished[seat] ?? 0)}/${faNum(TOKENS_PER_PLAYER)} ${fa.mensch.finishedTokens}`}
          style={{ display: "inline-flex", gap: 3, flex: "none" }}
        >
          {Array.from({ length: TOKENS_PER_PLAYER }, (_, i) => (
            <span
              key={i}
              aria-hidden
              className={
                litSeat === seat && i === (view.finished[seat] ?? 0) - 1
                  ? "mensch-home-flare"
                  : undefined
              }
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: i < (view.finished[seat] ?? 0) ? colour.base : "rgba(255,255,255,0.16)",
                boxShadow: i < (view.finished[seat] ?? 0) ? `0 0 4px ${colour.base}` : undefined,
              }}
            />
          ))}
        </span>
      </div>
    );
  };

  // «حرکتی نداری» is not a hint, it is bad NEWS — it has to look different from "your turn,
  // roll". Alerts get the error colour and a glyph so a failure is never mistaken for a prompt.
  const alerting = isMyTurn && !canRoll && view.noLegalMove;
  /**
   * The painted board: the track, the four home runs and the four yards.
   *
   * It depends ONLY on the geometry (seat count, rotation, cell size) — nothing about it can
   * change while a game runs. It was nevertheless being rebuilt as ~88 React elements on every
   * render: once per die tick, once per walk hop, once per server frame. Memoising it takes all
   * of that off the critical path.
   */
  const boardPaint = useMemo(
    () => (
      <>
        {/* Track — every ARM's start square wears that arm's colour, occupied or not. */}
        {track.map((c, i) => {
          const arm = MENSCH_ARMS.find((a) => startIndexForArm(a) === i);
          return square(
            `k-${i}`,
            c,
            arm === undefined ? "#fbfaf4" : (SEAT_COLORS[arm]?.base ?? "#fbfaf4"),
            arm === undefined ? undefined : "#fffdf0",
          );
        })}
        {/* Home runs + yards for ALL FOUR arms, always in their own colour. An arm nobody is
            sitting on is DECORATION: drawn so the board always reads as a منچ board, dimmed so
            you can still tell who is actually in the game. It holds no tokens and counts for
            nothing. */}
        {MENSCH_ARMS.map((arm) => {
          const colour = armTone(arm);
          const fade = seatOnArm(arm, seatCount) === null ? 0.42 : 1;
          return [
            ...homeCellsForArm(arm).map((c, i) =>
              square(`h-${arm}-${i}`, c, colour.light, undefined, fade),
            ),
            ...yardCellsForArm(arm).map((c, i) =>
              square(`y-${arm}-${i}`, c, "rgba(0,0,0,0.30)", colour.base, fade),
            ),
          ];
        })}
      </>
    ),
    // `square`/`pos` are closures over exactly these; the board cannot change otherwise.
    [seatCount, turns, cellPx],
  );

  const hint = !isMyTurn
    ? fa.mensch.turnOpponent
    : // R6 — an unplayable 6 rolls AGAIN (checked before canRoll, since it also sets canRoll).
      view.mustReroll
      ? fa.mensch.hintReroll
      : canRoll
        ? fa.mensch.hintRoll
        : view.noLegalMove
          ? `⚠️ ${fa.mensch.hintNoMove}`
          : fa.mensch.hintMove.replace("{die}", faNum(view.die ?? 0));

  return (
    <div
      className="mx-auto flex w-full max-w-[440px] flex-col overflow-hidden text-on-surface select-none"
      style={{
        height: "var(--game-h)",
        background:
          "radial-gradient(circle at center,rgb(var(--c-room-light)) 0%,rgb(var(--c-surface-dim)) 100%)",
      }}
    >
      {/* HUD — exit (right in RTL) · centred «منچ» · the online turn countdown (left).
          ITEM 7 — the countdown lives on THIS line, beside the exit and chat buttons and the
          title, rather than floating over the board: it belongs with the other chrome, and the
          board is already full. The spacer keeps the title centred when there is no timer
          (offline, or an online table with nobody else in it). */}
      <header
        className="relative z-10 flex items-center justify-between px-4"
        style={{
          paddingTop: "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        }}
      >
        <button
          type="button"
          onClick={onLeave}
          aria-label={fa.mensch.leave}
          className={`${BTN} flex h-10 w-10 flex-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
        >
          <GameIcon type="exit" size={20} />
        </button>
        <span className="font-headline-md text-headline-md font-bold text-secondary">
          {fa.mensch.title}
        </span>
        {timer ? (
          <span className="flex h-10 flex-none items-center">
            <TurnTimer {...timer} compact />
          </span>
        ) : (
          <span className="h-10 w-10 flex-none" />
        )}
      </header>

      {/* One strip per SEATED player, two per row so even a full table fits a phone.
          Sits well clear of the «منچ» title — it used to crowd it. */}
      <div className="grid grid-cols-2 gap-1 px-3 pt-4">
        {Array.from({ length: seatCount }, (_, s) => strip(s))}
      </div>

      {/* BOARD */}
      {/* Board + hint travel together as one centred block. The hint used to live down by
          the controls, where it read as part of the roll BUTTON; hugging the board keeps it
          plainly clear of it and lets the pair sit in the middle of the screen. */}
      <div
        ref={fit.ref}
        className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-hidden px-2"
      >
        <div
          data-testid="mensch-board"
          style={{
            position: "relative",
            flex: "none",
            width: side,
            height: side,
            borderRadius: 12,
            background: "radial-gradient(circle, #134e38 0%, #0a2a1f 100%)",
            boxShadow: "inset 0 4px 12px rgba(0,0,0,0.8), 0 4px 10px rgba(0,0,0,0.5)",
          }}
        >
          {/* The painted board — memoised; see `boardPaint`. */}
          {boardPaint}

          {/* Capture impact — under the tokens, over the felt. */}
          {ripple ? (
            <span
              key={ripple.key}
              aria-hidden
              data-testid="mensch-capture-ripple"
              className="mensch-capture-ripple"
              style={{
                position: "absolute",
                left: pos({ col: ripple.col, row: ripple.row }).left + cellPx / 2,
                top: pos({ col: ripple.col, row: ripple.row }).top + cellPx / 2,
                width: cellPx * 2,
                height: cellPx * 2,
                marginLeft: -cellPx,
                marginTop: -cellPx,
                borderRadius: "50%",
                pointerEvents: "none",
                zIndex: 4,
              }}
            />
          ) : null}

          {/* Tokens, on top of everything. */}
          {view.tokens.map((row, seat) =>
            row.map((step, i) =>
              step === YARD || step < TRACK_LEN + 4 ? token(seat, i, step) : null,
            ),
          )}
        </div>

        {/* The die sits UP here with the board, not down in the control row — and it appears
            only once one has actually been THROWN. Fixed height so the board never resizes. */}
        <div
          style={{
            height: 48,
            flex: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* The slot is ALWAYS mounted, and merely INVISIBLE until a die has been thrown.
              Mounting it on the first throw meant that throw had to create the compositor
              layer and rasterise six faces while animating — which is what made the very
              first roll janky. Pre-warmed like this, the first throw is as smooth as the rest.
              `visibility` (not conditional rendering) keeps the layer alive without ever
              showing an empty die box. */}
          <span
            data-testid="mensch-die-slot"
            data-roller={rollerSeat === you ? "you" : "opponent"}
            data-shown={shownDie !== null ? "true" : "false"}
            aria-hidden={shownDie === null}
            /* A roomy gap: the label used to crowd the die. */
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 14,
              visibility: shownDie !== null ? "visible" : "hidden",
            }}
          >
            <Die3D
              key={throwId}
              value={shownDie ?? 1}
              size={40}
              seed={`mensch-${view.rollCount}`}
              throwing={rolling}
              /* Your die is thrown up from YOUR edge of the table; an opponent's comes down
                   from theirs — so whose roll it is reads from the motion itself. */
              fromBottom={rollerSeat === you}
              launch={80}
              /* ONE renderer on every platform. The CSS cube turned out to be a per-platform
                 lottery: flat on Android, then buggy on iOS once iOS was the only platform
                 still running it. The drawn die behaves identically everywhere, so منچ takes
                 it outright instead of branching on what a WebView claims it can do. */
              alwaysDrawn
              testId="mensch-die"
            />
            <span
              data-testid="mensch-roller"
              style={{
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: "nowrap",
                color: seatTone(rollerSeat).base,
              }}
            >
              {rollerSeat === you ? fa.mensch.yourRoll : fa.mensch.theirRoll}
            </span>
          </span>
        </div>

        {/* Contextual hint — FIXED height, single clipped line, so the board never resizes. */}
        <div
          data-testid="mensch-hint"
          data-alert={alerting ? "true" : undefined}
          role={alerting ? "alert" : undefined}
          className={`px-3 text-center font-bold ${alerting ? "text-error" : "text-secondary"}`}
          style={{
            height: 30,
            flex: "none",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            fontSize: 14,
            lineHeight: "30px",
          }}
        >
          {view.status === "playing" ? hint : ""}
        </div>
        {/* ITEM 6 — the CAPTURE announcement sits between the instruction line and the roll
            button, travelling with the board block rather than pinned above the controls
            (where it read as part of the button). Its own reserved row: a capture is a
            one-off event, not a rolling status, so it never shares a line with the hint. */}
        <div
          style={{
            height: 30,
            flex: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingInline: 12,
          }}
        >
          {capture ? (
            <span
              key={capture.key}
              data-testid="mensch-capture-notice"
              role="status"
              className="mensch-capture-notice"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                maxWidth: "100%",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                fontSize: 13,
                fontWeight: 800,
                padding: "3px 12px",
                borderRadius: 999,
                // Tinted with the CAPTURING seat's colour, so you can see at a glance who struck.
                color: capture.of === you ? "#fff2f0" : "#1a0f0a",
                background:
                  capture.of === you
                    ? "rgb(var(--c-error))"
                    : seatColor(capture.by, seatCount).base,
                boxShadow: "0 2px 8px rgba(0,0,0,.45)",
              }}
            >
              {capture.of === you
                ? `💥 ${fa.mensch.captureOnYou.replace("{name}", seatName(capture.by))}`
                : capture.by === you
                  ? `🎯 ${fa.mensch.captureByYou.replace("{name}", seatName(capture.of))}`
                  : `🎯 ${fa.mensch.captureOther
                      .replace("{by}", seatName(capture.by))
                      .replace("{of}", seatName(capture.of))}`}
            </span>
          ) : null}
        </div>
      </div>

      {/* Controls — FIXED height reserve, so showing/hiding a button cannot move the board. */}
      <div
        className="flex items-center justify-center gap-3 px-3"
        style={{
          height: 66,
          flex: "none",
          paddingBottom: "calc(10px + var(--game-safe-bottom))",
        }}
      >
        {canRoll ? (
          <Button
            variant="primary"
            /* The throw answers with dice landing on the felt, so the generic UI tap would be a
               SECOND sound for one action — you heard a click, then a clatter. */
            silent
            onClick={() => onMove({ kind: "roll" })}
          >
            🎲 {fa.mensch.roll}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
